
import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/Button';
import { MessageCircle, ShieldCheck, Loader2, Play, Bell, Camera, X, Image as ImageIcon, AlertTriangle, Phone, Navigation, History, CheckCircle } from 'lucide-react';
import { JobRequest, JobStatus, PlatformFeeConfig } from '../types';
import { updateJobStatus } from '../services/firestoreService';
import { useTranslation } from '../translations';
import { WorkerPaymentCollectionModal } from './WorkerPaymentCollectionModal';
import { notificationService } from '../services/notificationService';

interface WorkerJobRequestsProps {
    availableBroadcasts?: JobRequest[];
    activeJobsList: JobRequest[];
    onAcceptBroadcast?: (job: JobRequest) => Promise<void> | void;
    onChat?: (job: JobRequest) => void;
    onTrackJob?: (job: JobRequest) => void;
    onCancelJob?: (jobId: string) => void;
    feeConfig?: PlatformFeeConfig;
    workerUpiId?: string;
}

import { useLocation } from 'react-router-dom';

export const WorkerJobRequests: React.FC<WorkerJobRequestsProps> = ({
    availableBroadcasts = [], activeJobsList = [], onAcceptBroadcast, onChat, onTrackJob, onCancelJob, feeConfig, workerUpiId
}) => {
    const location = useLocation();
    const [activeTab, setActiveTab] = useState<'requests' | 'active' | 'history'>((location.state as any)?.tab || 'requests');

    // ✅ FIX: Listen to location state changes to switch tabs (e.g. after Accepting a job)
    useEffect(() => {
        if ((location.state as any)?.tab) {
            setActiveTab((location.state as any).tab);
        }
    }, [location.state]);
    const [verifyingJobId, setVerifyingJobId] = useState<string | null>(null);
    const [verificationType, setVerificationType] = useState<'START' | 'FINISH'>('START');
    const [otp, setOtp] = useState(['', '', '', '']);
    const [isVerifying, setIsVerifying] = useState(false);
    const [isRequestingCompletion, setIsRequestingCompletion] = useState<string | null>(null);
    const [acceptingJobId, setAcceptingJobId] = useState<string | null>(null);

    // Price Negotiation State
    const [priceInputs, setPriceInputs] = useState<Record<string, string>>({});
    const [isSettingPrice, setIsSettingPrice] = useState<string | null>(null);

    // Payment Collection State
    const [collectingPaymentJobId, setCollectingPaymentJobId] = useState<string | null>(null);

    // Proof Upload State
    const [showProofModal, setShowProofModal] = useState<string | null>(null); // jobId
    const [proofMedia, setProofMedia] = useState<string[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);

    // Cancellation State
    const [cancellingJob, setCancellingJob] = useState<JobRequest | null>(null);

    const lang = (localStorage.getItem('sl_lang') || 'en') as any;
    const t = useTranslation(lang);

    // Filter Jobs with explicit JobStatus typing
    const activeStatuses: JobStatus[] = ['ACCEPTED', 'IN_PROGRESS', 'OTP_VERIFIED'];
    const historyStatuses: JobStatus[] = ['COMPLETED', 'CANCELLED'];

    // Optimistic UI state
    const [optimisticJobs, setOptimisticJobs] = useState<Record<string, Partial<JobRequest>>>({});

    // Sync optimistic jobs with incoming props (remove if prop status matches)
    useEffect(() => {
        setOptimisticJobs(prev => {
            const next = { ...prev };
            let changed = false;
            Object.keys(next).forEach(id => {
                const job = activeJobsList.find(j => j.id === id);
                if (job && job.status === next[id].status && (next[id].isPaid === undefined || job.isPaid === next[id].isPaid)) {
                    delete next[id];
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [activeJobsList]);

    const getDisplayJobs = () => {
        return activeJobsList.map(job => {
            if (optimisticJobs[job.id]) {
                return { ...job, ...optimisticJobs[job.id] };
            }
            return job;
        });
    };

    const currentJobs = getDisplayJobs();
    const ongoingJobs = currentJobs.filter(j =>
        (['ACCEPTED', 'IN_PROGRESS', 'OTP_VERIFIED'] as JobStatus[]).includes(j.status) ||
        (j.status === 'COMPLETED' && !j.isPaid)
    );
    const historyJobs = currentJobs.filter(j => historyStatuses.includes(j.status) && j.isPaid);

    // Track previous job count to detect new jobs
    const prevJobCountRef = useRef(availableBroadcasts.length);

    // Show notification when new job arrives
    useEffect(() => {
        const currentCount = availableBroadcasts.length;
        const prevCount = prevJobCountRef.current;

        // If new job arrived (count increased)
        if (currentCount > prevCount && availableBroadcasts.length > 0) {
            const newestJob = availableBroadcasts[0]; // Assuming newest is first

            // Show persistent notification
            notificationService.showJobNotification({
                jobId: newestJob.id,
                title: 'New Job Request',
                customerName: newestJob.customerName || 'Customer',
                skill: newestJob.skill,
                isBroadcast: true
            });
        }

        // Update ref
        prevJobCountRef.current = currentCount;
    }, [availableBroadcasts]);

    const handleAccept = async (job: JobRequest) => {
        if (!onAcceptBroadcast) return;
        setAcceptingJobId(job.id);

        // Optimistic UI for broadcast acceptance (if we had a local list for broadcast)
        // Since broadcasts are list-based, we'll just wait for the acceptance which moves it to activeJobs

        try {
            await onAcceptBroadcast(job);
            await notificationService.closeNotification(`job-${job.id}`);
            setActiveTab('active'); // Switch to active tab so worker sees the new job immediately
        } catch (error: any) {
            console.error("Accept failed", error);
            alert(error.message);
        } finally {
            setAcceptingJobId(null);
        }
    };

    const submitOtp = async () => {
        const entered = otp.join('');
        const job = currentJobs.find(j => j.id === verifyingJobId);

        if (!job || !verifyingJobId) {
            alert("Job not found.");
            setVerifyingJobId(null);
            return;
        }

        const target = verificationType === 'START' ? job.otp : job.completionOtp;

        if (entered !== target) return alert("OTP Security Mismatch");

        const newStatus: JobStatus = verificationType === 'START' ? 'IN_PROGRESS' : 'COMPLETED';

        // Optimistic update
        setOptimisticJobs(prev => ({
            ...prev,
            [verifyingJobId]: { status: newStatus }
        }));
        setVerifyingJobId(null);
        setOtp(['', '', '', '']);

        try {
            await updateJobStatus(verifyingJobId, newStatus);
            if (verificationType === 'FINISH') {
                setCollectingPaymentJobId(verifyingJobId);
            }
        } catch (e) {
            // Revert on failure
            setOptimisticJobs(prev => {
                const next = { ...prev };
                delete next[verifyingJobId];
                return next;
            });
            alert("Protocol verification failed. Try again.");
        }
    };

    const handleSetPrice = async (jobId: string) => {
        const price = priceInputs[jobId];
        if (!price || isNaN(Number(price)) || Number(price) <= 0) {
            alert("Please enter a valid price.");
            return;
        }

        setIsSettingPrice(jobId);
        try {
            await updateJobStatus(jobId, 'ACCEPTED', {
                price: Number(price),
                priceSetByWorker: true
            });
            // Optimistic update
            setOptimisticJobs(prev => ({
                ...prev,
                [jobId]: { ...prev[jobId], priceSetByWorker: true, price: Number(price) }
            }));
        } catch (error) {
            console.error(error);
            alert("Failed to send price.");
        } finally {
            setIsSettingPrice(null);
        }
    };

    const handlePaymentCollectionComplete = async (method: 'CASH' | 'ONLINE') => {
        if (collectingPaymentJobId) {
            const jobId = collectingPaymentJobId;

            // Optimistic
            setOptimisticJobs(prev => ({
                ...prev,
                [jobId]: { isPaid: true, status: 'COMPLETED' }
            }));
            setCollectingPaymentJobId(null);

            try {
                const job = currentJobs.find(j => j.id === jobId);
                const price = job ? Number(job.price) : 0;

                await updateJobStatus(jobId, 'COMPLETED', {
                    isPaid: true,
                    paymentMethod: method,
                    platformFee: 0,
                    workerEarnings: price
                });
            } catch (e) {
                setOptimisticJobs(prev => {
                    const next = { ...prev };
                    delete next[jobId];
                    return next;
                });
                alert("Payment status sync failed.");
            }
        }
    };

    const handleOtpChange = (index: number, value: string) => {
        if (value.length > 1 || isNaN(Number(value))) return;
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);
        if (value && index < 3) {
            document.getElementById(`otp-input-${index + 1}`)?.focus();
        }
    };

    const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            document.getElementById(`otp-input-${index - 1}`)?.focus();
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            Array.from(e.target.files).forEach(file => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    if (reader.result) setProofMedia(prev => [...prev, reader.result as string]);
                };
                reader.readAsDataURL(file as Blob);
            });
        }
    };

    const removeProof = (index: number) => {
        setProofMedia(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmitProofAndRequest = async () => {
        if (!showProofModal) return;
        if (proofMedia.length === 0) return alert("Please upload at least one photo of the completed work.");

        setIsRequestingCompletion(showProofModal);
        await updateJobStatus(showProofModal, 'IN_PROGRESS', {
            completionRequested: true,
            completionMediaUrls: proofMedia
        });
        setIsRequestingCompletion(null);
        setShowProofModal(null);
        setProofMedia([]);
    };

    const confirmCancellation = () => {
        if (cancellingJob && onCancelJob) {
            onCancelJob(cancellingJob.id);
            setCancellingJob(null);
        }
    };

    const jobForPayment = collectingPaymentJobId ? activeJobsList.find(j => j.id === collectingPaymentJobId) : null;

    return (
        <div className="h-full flex flex-col bg-slate-50 dark:bg-slate-950">
            <div className="bg-white dark:bg-slate-900 p-6 border-b border-slate-100 dark:border-slate-800">
                <h1 className="text-2xl font-black text-slate-900 dark:text-white mb-4">{t.tasks}</h1>
                <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-2xl">
                    <button onClick={() => setActiveTab('requests')} className={`flex-1 py-3 text-xs font-black uppercase rounded-xl transition-all ${activeTab === 'requests' ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-slate-400'}`}>New Signal</button>
                    <button onClick={() => setActiveTab('active')} className={`flex-1 py-3 text-xs font-black uppercase rounded-xl transition-all flex items-center justify-center gap-1 ${activeTab === 'active' ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-slate-400'}`}>
                        Active
                        {ongoingJobs.length > 0 && <span className="bg-blue-600 text-white text-[8px] w-4 h-4 flex items-center justify-center rounded-full ml-1">{ongoingJobs.length}</span>}
                    </button>
                    <button onClick={() => setActiveTab('history')} className={`flex-1 py-3 text-xs font-black uppercase rounded-xl transition-all ${activeTab === 'history' ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-slate-400'}`}>History</button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {activeTab === 'requests' && availableBroadcasts.map(job => (
                    <div key={job.id} className="bg-white dark:bg-slate-900 rounded-[2rem] p-6 shadow-xl border-2 border-blue-50 dark:border-blue-900/30">
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <h3 className="font-black text-slate-900 dark:text-white text-xl">{job.skill} Work</h3>
                                <p className="text-[10px] text-blue-600 dark:text-blue-400 font-black uppercase tracking-widest">{job.customerName || 'Customer'}</p>
                            </div>
                            <span className="font-black text-green-600 dark:text-green-400 text-2xl">₹{job.price}</span>
                        </div>
                        <div className="flex gap-3">
                            <Button
                                variant="outline"
                                className="flex-1 h-14 rounded-2xl border-red-100 dark:border-red-900/50 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                                onClick={async () => {
                                    if (confirm("Ignore this job? It will be removed from your list.")) {
                                        try {
                                            const { data: { user } } = await import('../services/supabaseClient').then(m => m.supabase.auth.getUser());
                                            if (user) await import('../services/firestoreService').then(m => m.ignoreJob(job.id, user.id));
                                        } catch (e) { console.error(e); }
                                    }
                                }}
                            >
                                <X className="w-5 h-5" />
                            </Button>
                            <Button
                                fullWidth
                                onClick={() => handleAccept(job)}
                                disabled={acceptingJobId === job.id}
                                className="h-14 rounded-2xl flex-[4]"
                            >
                                {acceptingJobId === job.id ? <Loader2 className="animate-spin w-5 h-5" /> : 'Accept Signal'}
                            </Button>
                        </div>
                    </div>
                ))}

                {activeTab === 'active' && ongoingJobs.map(job => (
                    <div key={job.id} className="bg-white dark:bg-slate-900 rounded-[2rem] p-6 shadow-lg border border-slate-100 dark:border-slate-800 relative">
                        <div className="flex gap-4 mb-6">
                            <img src={job.customerAvatar} className="w-12 h-12 rounded-2xl object-cover" />
                            <div className="flex-1">
                                <div className="flex justify-between items-start">
                                    <h3 className="font-black text-slate-900 dark:text-white">{job.customerName || 'Client'}</h3>
                                    <button
                                        onClick={() => setCancellingJob(job)}
                                        className="text-red-400 hover:text-red-600 p-1 bg-red-50 dark:bg-red-900/20 rounded-lg text-[10px] font-black uppercase tracking-wider"
                                    >
                                        Cancel
                                    </button>
                                </div>
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">{job.status}</p>
                            </div>
                        </div>

                        <div className="flex gap-2 mb-3">
                            <Button variant="outline" size="sm" className="flex-1 rounded-xl dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700" onClick={() => {
                                const target = job.customerPhone;
                                if (target && /^\+?[\d\s-]{10,}$/.test(target)) {
                                    window.location.href = `tel:${target.replace(/[^0-9+]/g, '')}`;
                                } else {
                                    alert("Customer phone number not available.");
                                }
                            }}><Phone className="w-4 h-4 mr-1.5" /> Call</Button>
                            <Button variant="outline" size="sm" className="flex-1 rounded-xl dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700" onClick={() => onTrackJob?.(job)}><Navigation className="w-4 h-4 mr-1.5" /> Track</Button>
                            <Button variant="outline" size="sm" className="flex-1 rounded-xl dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700" onClick={() => onChat?.(job)}><MessageCircle className="w-4 h-4 mr-1.5" /> Chat</Button>
                        </div>

                        <div className="w-full">
                            {job.status === 'ACCEPTED' ? (
                                !job.priceSetByWorker ? (
                                    <div className="flex flex-col gap-2 p-3 bg-blue-50/50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-900/50">
                                        <label className="text-xs font-black text-blue-700 dark:text-blue-400">Set Agreed Price (₹)</label>
                                        <div className="flex gap-2">
                                            <input
                                                type="number"
                                                value={priceInputs[job.id] || ''}
                                                onChange={e => setPriceInputs(prev => ({ ...prev, [job.id]: e.target.value }))}
                                                className="flex-1 rounded-lg border-2 border-slate-200 dark:border-slate-700 px-3 py-2 text-sm font-bold dark:bg-slate-800 dark:text-white focus:border-blue-500 outline-none"
                                                placeholder="e.g. 500"
                                            />
                                            <Button
                                                onClick={() => handleSetPrice(job.id)}
                                                disabled={isSettingPrice === job.id}
                                                className="rounded-lg bg-blue-600 px-4"
                                            >
                                                {isSettingPrice === job.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send'}
                                            </Button>
                                        </div>
                                    </div>
                                ) : !job.priceAcceptedByCustomer ? (
                                    <div className="p-3 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded-xl text-xs font-bold text-center border border-amber-100 dark:border-amber-900/50 animate-pulse">
                                        Waiting for customer to accept price (₹{job.price})...
                                    </div>
                                ) : (
                                    <Button fullWidth onClick={() => { setVerifyingJobId(job.id); setVerificationType('START'); }} className="rounded-xl bg-blue-600 shadow-blue-600/20"><Play className="w-4 h-4 mr-2" /> Start Work</Button>
                                )
                            ) : job.status === 'IN_PROGRESS' && !job.completionRequested ? (
                                <Button fullWidth onClick={() => setShowProofModal(job.id)} disabled={isRequestingCompletion === job.id} className="rounded-xl bg-slate-900 dark:bg-slate-700">
                                    {isRequestingCompletion === job.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Bell className="w-4 h-4 mr-2" /> Request Completion</>}
                                </Button>
                            ) : job.status === 'IN_PROGRESS' && job.completionRequested ? (
                                <Button fullWidth onClick={() => { setVerifyingJobId(job.id); setVerificationType('FINISH'); }} className="rounded-xl bg-green-600 shadow-green-600/20"><ShieldCheck className="w-4 h-4 mr-2" /> Enter Completion OTP</Button>
                            ) : job.status === 'OTP_VERIFIED' ? (
                                <div className="p-3 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-xl text-[10px] font-black uppercase tracking-widest text-center border border-green-100 dark:border-green-900/50">Work Verified - Collecting Payment...</div>
                            ) : job.status === 'COMPLETED' && !job.isPaid ? (
                                <Button fullWidth onClick={() => setCollectingPaymentJobId(job.id)} className="rounded-xl bg-green-600 shadow-green-600/20 animate-pulse">
                                    <CheckCircle className="w-4 h-4 mr-2" /> Collect Payment {job.paymentMethod ? '(User Paid)' : ''}
                                </Button>
                            ) : null}
                        </div>
                    </div>
                ))}

                {activeTab === 'history' && historyJobs.map(job => (
                    <div key={job.id} className="bg-white dark:bg-slate-900 rounded-[2rem] p-6 shadow-sm border border-slate-100 dark:border-slate-800 opacity-80">
                        <div className="flex justify-between items-center mb-4">
                            <div className="flex items-center gap-3">
                                <img src={job.customerAvatar} className="w-10 h-10 rounded-xl object-cover grayscale" />
                                <div>
                                    <h3 className="font-bold text-slate-900 dark:text-white text-sm">{job.customerName || 'Client'}</h3>
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{job.date}</p>
                                </div>
                            </div>
                            <span className={`px-2 py-1 rounded text-[10px] font-black uppercase tracking-widest ${job.status === 'COMPLETED' ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'}`}>
                                {job.status}
                            </span>
                        </div>
                        <div className="flex justify-between items-center bg-slate-50 dark:bg-slate-800 p-3 rounded-xl">
                            <span className="text-xs font-bold text-slate-600 dark:text-slate-400">{job.skill} Work</span>
                            <span className="text-sm font-black text-slate-900 dark:text-white">₹{job.price}</span>
                        </div>
                        {job.status === 'COMPLETED' && (
                            <div className="mt-4 flex items-center gap-2 text-green-600 dark:text-green-400 text-[10px] font-black uppercase tracking-widest justify-center">
                                <CheckCircle className="w-4 h-4" /> Work Finished & Paid
                            </div>
                        )}
                    </div>
                ))}

                {activeTab === 'active' && ongoingJobs.length === 0 && (
                    <div className="text-center py-20 text-slate-400">
                        <Play className="w-12 h-12 mx-auto mb-3 opacity-20" />
                        <p className="text-xs font-black uppercase tracking-widest">No Active Work</p>
                    </div>
                )}

                {activeTab === 'history' && historyJobs.length === 0 && (
                    <div className="text-center py-20 text-slate-400">
                        <History className="w-12 h-12 mx-auto mb-3 opacity-20" />
                        <p className="text-xs font-black uppercase tracking-widest">No Job History</p>
                    </div>
                )}
            </div>

            {/* Payment Collection Modal */}
            {jobForPayment && (
                <WorkerPaymentCollectionModal
                    job={jobForPayment}
                    onClose={() => setCollectingPaymentJobId(null)}
                    onPaymentComplete={handlePaymentCollectionComplete}
                    qrCodeUrl={undefined}
                    upiId={workerUpiId}
                />
            )}

            {/* OTP Verification Modal */}
            {verifyingJobId && (
                <div className="fixed inset-0 z-[5000] flex items-center justify-center p-6 bg-slate-900/80 backdrop-blur-md">
                    <div className="bg-white rounded-[2.5rem] w-full max-w-sm p-10 text-center animate-scaleIn">
                        <h2 className="text-2xl font-black mb-6">{verificationType === 'START' ? 'Start Protocol' : 'Finish Protocol'}</h2>
                        <p className="text-xs text-slate-500 font-bold mb-8">
                            {verificationType === 'START'
                                ? 'Ask customer for the arrival OTP to begin work.'
                                : 'Ask customer for the completion OTP to finish job.'}
                        </p>
                        <div className="flex gap-3 justify-center mb-10">
                            {otp.map((d, i) => (
                                <input
                                    key={i}
                                    id={`otp-input-${i}`}
                                    maxLength={1}
                                    value={d}
                                    onChange={e => handleOtpChange(i, e.target.value)}
                                    onKeyDown={e => handleOtpKeyDown(i, e)}
                                    className="w-12 h-16 border-2 rounded-2xl text-center text-2xl font-black focus:border-blue-600 outline-none transition-colors"
                                />
                            ))}
                        </div>
                        <div className="space-y-3">
                            <Button fullWidth onClick={submitOtp} disabled={isVerifying} className="h-16 rounded-2xl">
                                {isVerifying ? <Loader2 className="animate-spin" /> : 'Verify'}
                            </Button>
                            <Button variant="ghost" fullWidth onClick={() => setVerifyingJobId(null)}>Cancel</Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Proof of Work Modal */}
            {showProofModal && (
                <div className="fixed inset-0 z-[5000] flex items-center justify-center p-6 bg-slate-900/80 backdrop-blur-md">
                    <div className="bg-white rounded-[2.5rem] w-full max-w-sm overflow-hidden animate-scaleIn flex flex-col max-h-[85vh]">
                        <div className="p-8 pb-4">
                            <h2 className="text-2xl font-black text-slate-900">Proof of Work</h2>
                            <p className="text-xs text-slate-500 font-bold mt-2 leading-relaxed">
                                Upload photos or videos of the completed task to request completion code from customer.
                            </p>
                        </div>

                        <div className="flex-1 overflow-y-auto px-8 pb-4 scrollbar-hide">
                            <div className="grid grid-cols-2 gap-3 mb-4">
                                <button
                                    onClick={() => cameraInputRef.current?.click()}
                                    className="aspect-square rounded-2xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-blue-500 hover:text-blue-500 hover:bg-blue-50 transition-all"
                                >
                                    <Camera className="w-6 h-6 mb-2" />
                                    <span className="text-[10px] font-black uppercase">Camera</span>
                                </button>
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    className="aspect-square rounded-2xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-blue-500 hover:text-blue-500 hover:bg-blue-50 transition-all"
                                >
                                    <ImageIcon className="w-6 h-6 mb-2" />
                                    <span className="text-[10px] font-black uppercase">Gallery</span>
                                </button>
                                {proofMedia.map((url, idx) => (
                                    <div key={idx} className="relative aspect-square rounded-2xl overflow-hidden group">
                                        {url.startsWith('data:video') ? (
                                            <video src={url} className="w-full h-full object-cover" />
                                        ) : (
                                            <img src={url} className="w-full h-full object-cover" alt="" />
                                        )}
                                        <button
                                            onClick={() => removeProof(idx)}
                                            className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <input type="file" ref={fileInputRef} className="hidden" multiple accept="image/*,video/*" onChange={handleFileSelect} />
                            <input type="file" ref={cameraInputRef} className="hidden" accept="image/*" capture="environment" onChange={handleFileSelect} />
                        </div>

                        <div className="p-8 pt-4 border-t border-slate-100">
                            <Button
                                fullWidth
                                onClick={handleSubmitProofAndRequest}
                                disabled={proofMedia.length === 0 || isRequestingCompletion !== null}
                                className="h-16 rounded-2xl shadow-xl shadow-slate-900/20"
                            >
                                {isRequestingCompletion ? <Loader2 className="animate-spin" /> : 'Send Request'}
                            </Button>
                            <button onClick={() => { setShowProofModal(null); setProofMedia([]); }} className="w-full text-center mt-4 text-xs font-black text-slate-400 uppercase tracking-widest hover:text-slate-600">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cancellation Warning Modal */}
            {cancellingJob && (
                <div className="fixed inset-0 z-[5000] flex items-center justify-center p-6 bg-slate-900/80 backdrop-blur-md">
                    <div className="bg-white rounded-[2.5rem] w-full max-w-sm p-8 text-center animate-scaleIn">
                        <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-6 text-red-500">
                            <AlertTriangle className="w-8 h-8" />
                        </div>
                        <h2 className="text-xl font-black text-slate-900 mb-2">Cancel Active Job?</h2>
                        <p className="text-sm text-slate-500 leading-relaxed mb-8">
                            Cancelling an accepted job affects your reliability score.
                        </p>
                        <div className="space-y-3">
                            <Button variant="danger" fullWidth onClick={confirmCancellation} className="h-14 rounded-2xl shadow-xl shadow-red-600/20">
                                Yes, Cancel Job
                            </Button>
                            <Button variant="ghost" fullWidth onClick={() => setCancellingJob(null)} className="font-black text-slate-400">
                                Back
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
